<template>
  <div class="category-seo-content">
    <!-- 服务优势部分 -->
    <section class="advantages-section">
      <div class="section-header">
        <h2 class="section-title">
          <Icon name="material-symbols:star" size="28" />
          {{ getAdvantagesTitle() }}
        </h2>
        <p class="section-subtitle">
          {{ getAdvantagesSubtitle() }}
        </p>
      </div>

      <div class="advantages-list">
        <div
          v-for="advantage in getCategoryAdvantages()"
          :key="advantage.title"
          class="advantage-item"
        >
          <div class="advantage-icon">
            <Icon :name="advantage.icon" size="48" />
          </div>
          <div class="advantage-content">
            <h3 class="advantage-title">{{ advantage.title }}</h3>
            <p class="advantage-description">{{ advantage.description }}</p>
            <ul class="advantage-highlights">
              <li v-for="highlight in advantage.highlights" :key="highlight">
                <Icon name="material-symbols:check-circle" size="16" />
                {{ highlight }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- 适用场景部分 -->
    <section class="scenarios-section">
      <div class="section-header">
        <h2 class="section-title">
          <Icon name="material-symbols:business-center" size="28" />
          {{ $t('categorySeo.scenarios.title') }}
        </h2>
        <p class="section-subtitle">
          {{ getScenariosSubtitle() }}
        </p>
      </div>

      <div class="scenarios-grid">
        <div
          v-for="(scenario, index) in getCategoryScenarios()"
          :key="scenario.title"
          class="scenario-item"
        >
          <div class="scenario-image">
            <img
              :src="scenario.image"
              :alt="scenario.title"
              class="scenario-img"
            />
          </div>
          <div class="scenario-content">
            <h3 class="scenario-title">{{ scenario.title }}</h3>
            <p class="scenario-description">{{ scenario.description }}</p>
            <div class="scenario-features">
              <span
                v-for="feature in scenario.features"
                :key="feature"
                class="feature-tag"
              >
                {{ feature }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ部分 -->
    <section class="faq-section">
      <div class="section-header">
        <h2 class="section-title">
          <Icon name="material-symbols:help-center" size="28" />
          {{ $t('categorySeo.faq.title') }}
        </h2>
        <p class="section-subtitle">
          {{ $t('categorySeo.faq.subtitle', { name: getCategoryDisplayName() }) }}
        </p>
      </div>

      <div class="faq-list">
        <div
          v-for="(faq, index) in getCategoryFAQs()"
          :key="index"
          class="faq-item"
        >
          <div
            class="faq-question"
            @click="toggleFaq(index)"
          >
            <h3 class="question-text">{{ faq.question }}</h3>
            <Icon
              name="material-symbols:expand-more"
              size="24"
              class="expand-icon"
              :class="{ rotated: activeFaq === index }"
            />
          </div>
          <div
            class="faq-answer"
            :class="{ expanded: activeFaq === index }"
          >
            <div class="answer-content">
              <p>{{ faq.answer }}</p>
              <div v-if="faq.extra" class="answer-extra">
                <ul>
                  <li v-for="item in faq.extra" :key="item">{{ item }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

// 组件props
interface Props {
  category?: string
}

const props = withDefaults(defineProps<Props>(), {
  category: 'pdf'
})

// FAQ展开状态
const activeFaq = ref<number | null>(null)
const { t, te } = useI18n()

// 构建可用的 extra 列表（根据已存在的 i18n 键动态收集）
const buildExtras = (baseKey: string, max = 8): string[] => {
  const items: string[] = []
  for (let i = 0; i < max; i++) {
    const key = `${baseKey}.${i}`
    if (te(key)) items.push(t(key))
  }
  return items
}

// 品类显示名称映射
const getCategoryDisplayName = (): string => {
  const names: Record<string, string> = {
    pdf: 'PDF',
    office: t('categorySeo.display.office'),
    ebook: t('categorySeo.display.ebook'),
    subtitle: t('categorySeo.display.subtitle')
  }
  const key = (props.category ?? 'pdf') as keyof typeof names
  return names[key] ?? 'PDF'
}

// 获取优势部分标题
const getAdvantagesTitle = (): string => {
  const titles: Record<string, string> = {
    pdf: t('categorySeo.advantages.pdf.title'),
    office: t('categorySeo.advantages.office.title'),
    ebook: t('categorySeo.advantages.ebook.title'),
    subtitle: t('categorySeo.advantages.subtitle.title')
  }
  const key = (props.category ?? 'pdf') as keyof typeof titles
  return titles[key] ?? titles.pdf ?? ''
}

// 获取优势部分副标题
const getAdvantagesSubtitle = (): string => {
  const subtitles: Record<string, string> = {
    pdf: t('categorySeo.advantages.pdf.subtitle'),
    office: t('categorySeo.advantages.office.subtitle'),
    ebook: t('categorySeo.advantages.ebook.subtitle'),
    subtitle: t('categorySeo.advantages.subtitle.subtitle')
  }
  const key = (props.category ?? 'pdf') as keyof typeof subtitles
  return subtitles[key] ?? subtitles.pdf ?? ''
}

// 获取场景部分副标题
const getScenariosSubtitle = (): string => {
  const subtitles: Record<string, string> = {
    pdf: t('categorySeo.scenarios.subtitle.pdf'),
    office: t('categorySeo.scenarios.subtitle.office'),
    ebook: t('categorySeo.scenarios.subtitle.ebook'),
    subtitle: t('categorySeo.scenarios.subtitle.subtitle')
  }
  const key = (props.category ?? 'pdf') as keyof typeof subtitles
  return subtitles[key] ?? subtitles.pdf ?? ''
}

// 获取品类优势数据
const getCategoryAdvantages = () => {
  const advantages = {
    pdf: [
      {
        title: t('categorySeo.advantages.pdf.items.0.title'),
        icon: 'material-symbols:format-paint',
        description: t('categorySeo.advantages.pdf.items.0.description'),
        highlights: [
          t('categorySeo.advantages.pdf.items.0.highlights.0'),
          t('categorySeo.advantages.pdf.items.0.highlights.1'),
          t('categorySeo.advantages.pdf.items.0.highlights.2'),
          t('categorySeo.advantages.pdf.items.0.highlights.3')
        ]
      },
      {
        title: t('categorySeo.advantages.pdf.items.1.title'),
        icon: 'material-symbols:picture-as-pdf',
        description: t('categorySeo.advantages.pdf.items.1.description'),
        highlights: [
          t('categorySeo.advantages.pdf.items.1.highlights.0'),
          t('categorySeo.advantages.pdf.items.1.highlights.1'),
          t('categorySeo.advantages.pdf.items.1.highlights.2'),
          t('categorySeo.advantages.pdf.items.1.highlights.3')
        ]
      },
      {
        title: t('categorySeo.advantages.pdf.items.2.title'),
        icon: 'material-symbols:translate',
        description: t('categorySeo.advantages.pdf.items.2.description'),
        highlights: [
          t('categorySeo.advantages.pdf.items.2.highlights.0'),
          t('categorySeo.advantages.pdf.items.2.highlights.1'),
          t('categorySeo.advantages.pdf.items.2.highlights.2'),
          t('categorySeo.advantages.pdf.items.2.highlights.3')
        ]
      },
      {
        title: t('categorySeo.advantages.pdf.items.3.title'),
        icon: 'material-symbols:support-agent',
        description: t('categorySeo.advantages.pdf.items.3.description'),
        highlights: [
          t('categorySeo.advantages.pdf.items.3.highlights.0'),
          t('categorySeo.advantages.pdf.items.3.highlights.1'),
          t('categorySeo.advantages.pdf.items.3.highlights.2'),
          t('categorySeo.advantages.pdf.items.3.highlights.3')
        ]
      }
    ]
  }

  return advantages[props.category as keyof typeof advantages] || advantages.pdf
}

// 获取品类场景数据
const getCategoryScenarios = () => {
  const scenarios = {
    pdf: [
      {
        title: t('categorySeo.scenarios.pdf.items.0.title'),
        description: t('categorySeo.scenarios.pdf.items.0.description'),
        image: '/images/pdf/pdf-scenario-1.jpg',
        features: [
          t('categorySeo.scenarios.pdf.items.0.features.0'),
          t('categorySeo.scenarios.pdf.items.0.features.1'),
          t('categorySeo.scenarios.pdf.items.0.features.2')
        ]
      },
      {
        title: t('categorySeo.scenarios.pdf.items.1.title'),
        description: t('categorySeo.scenarios.pdf.items.1.description'),
        image: '/images/pdf/pdf-scenario-2.jpg',
        features: [
          t('categorySeo.scenarios.pdf.items.1.features.0'),
          t('categorySeo.scenarios.pdf.items.1.features.1'),
          t('categorySeo.scenarios.pdf.items.1.features.2')
        ]
      },
      {
        title: t('categorySeo.scenarios.pdf.items.2.title'),
        description: t('categorySeo.scenarios.pdf.items.2.description'),
        image: '/images/pdf/pdf-scenario-3.jpg',
        features: [
          t('categorySeo.scenarios.pdf.items.2.features.0'),
          t('categorySeo.scenarios.pdf.items.2.features.1'),
          t('categorySeo.scenarios.pdf.items.2.features.2')
        ]
      },
      {
        title: t('categorySeo.scenarios.pdf.items.3.title'),
        description: t('categorySeo.scenarios.pdf.items.3.description'),
        image: '/images/pdf/pdf-scenario-4.jpg',
        features: [
          t('categorySeo.scenarios.pdf.items.3.features.0'),
          t('categorySeo.scenarios.pdf.items.3.features.1'),
          t('categorySeo.scenarios.pdf.items.3.features.2')
        ]
      }
    ]
  }

  return scenarios[props.category as keyof typeof scenarios] || scenarios.pdf
}

// 获取品类FAQ数据
const getCategoryFAQs = () => {
  const faqs = {
    pdf: [
      {
        question: t('categorySeo.faq.pdf.items.0.q'),
        answer: t('categorySeo.faq.pdf.items.0.a'),
        extra: buildExtras('categorySeo.faq.pdf.items.0.extra')
      },
      {
        question: t('categorySeo.faq.pdf.items.1.q'),
        answer: t('categorySeo.faq.pdf.items.1.a'),
        extra: buildExtras('categorySeo.faq.pdf.items.1.extra')
      },
      {
        question: t('categorySeo.faq.pdf.items.2.q'),
        answer: t('categorySeo.faq.pdf.items.2.a'),
        extra: buildExtras('categorySeo.faq.pdf.items.2.extra')
      },
      {
        question: t('categorySeo.faq.pdf.items.3.q'),
        answer: t('categorySeo.faq.pdf.items.3.a'),
        extra: buildExtras('categorySeo.faq.pdf.items.3.extra')
      },
      {
        question: t('categorySeo.faq.pdf.items.4.q'),
        answer: t('categorySeo.faq.pdf.items.4.a'),
        extra: buildExtras('categorySeo.faq.pdf.items.4.extra')
      },
      {
        question: t('categorySeo.faq.pdf.items.5.q'),
        answer: t('categorySeo.faq.pdf.items.5.a'),
        extra: buildExtras('categorySeo.faq.pdf.items.5.extra')
      }
    ]
  }

  return faqs[props.category as keyof typeof faqs] || faqs.pdf
}

// FAQ切换方法
const toggleFaq = (index: number) => {
  activeFaq.value = activeFaq.value === index ? null : index
}
</script>

<style scoped lang="scss">
.category-seo-content {
  width: 100%;
  padding: $spacing-xxl 0;
}

// 通用section样式
section {
  margin-bottom: $spacing-xxl;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-header {
  text-align: center;
  margin-bottom: $spacing-xl;

  .section-title {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $spacing-sm;
    font-size: 2rem;
    font-weight: 700;
    color: $gray-900;
    margin: 0 0 $spacing-md 0;
    background: linear-gradient(135deg, $primary-color 0%, $accent-color 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;

    .iconify {
      color: $accent-color !important;
      filter: drop-shadow(0 2px 4px rgba($accent-color, 0.3));
    }
  }

  .section-subtitle {
    font-size: 1.1rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.6;
  }
}

// 服务优势部分
.advantages-section {
  background: linear-gradient(135deg,
    rgba($white, 0.82) 0%,
    rgba($gray-50, 0.75) 50%,
    rgba($white, 0.82) 100%);
  padding: $spacing-xl;
  border-radius: $border-radius-lg;
  backdrop-filter: blur(8px);
  border: 1px solid rgba($primary-color, 0.2);

  .advantages-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: $spacing-xl;

    .advantage-item {
      display: flex;
      gap: $spacing-lg;
      padding: $spacing-lg;
      background: rgba($white, 0.85);
      border-radius: $border-radius-lg;
      box-shadow: $shadow-md, 0 0 18px rgba($primary-color, 0.12);
      border: 1px solid rgba($primary-color, 0.15);
      backdrop-filter: blur(5px);

      .advantage-icon {
        flex-shrink: 0;
        color: $primary-color;
      }

      .advantage-content {
        .advantage-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: $gray-900;
          margin: 0 0 $spacing-sm 0;
        }

        .advantage-description {
          font-size: 1rem;
          color: $gray-600;
          margin: 0 0 $spacing-md 0;
          line-height: 1.5;
        }

        .advantage-highlights {
          list-style: none;
          padding: 0;
          margin: 0;

          li {
            display: flex;
            align-items: center;
            gap: $spacing-xs;
            margin-bottom: $spacing-xs;
            font-size: 0.9rem;
            color: $gray-700;

            .iconify {
              color: $success;
              flex-shrink: 0;
            }
          }
        }
      }
    }
  }
}

// 适用场景部分
.scenarios-section {
  .scenarios-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: $spacing-xl;

    .scenario-item {
      background: rgba($white, 0.95);
      border-radius: $border-radius-lg;
      box-shadow: $shadow-md, 0 0 20px rgba($primary-color, 0.08);
      border: 1px solid rgba($primary-color, 0.15);
      backdrop-filter: blur(10px);
      overflow: hidden;
      transition: transform 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-4px);
        box-shadow: $shadow-lg, 0 0 25px rgba($primary-color, 0.15);
      }

      .scenario-image {
        width: 100%;
        height: 200px;
        overflow: hidden;

        .scenario-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        &:hover .scenario-img {
          transform: scale(1.05);
        }
      }

      .scenario-content {
        padding: $spacing-lg;

        .scenario-title {
          font-size: 1.2rem;
          font-weight: 600;
          color: $gray-900;
          margin: 0 0 $spacing-sm 0;
        }

        .scenario-description {
          font-size: 1rem;
          color: $gray-600;
          margin: 0 0 $spacing-md 0;
          line-height: 1.5;
        }

        .scenario-features {
          display: flex;
          flex-wrap: wrap;
          gap: $spacing-xs;

          .feature-tag {
            background: rgba($primary-color, 0.1);
            color: $primary-color;
            padding: $spacing-xs $spacing-sm;
            border-radius: $border-radius-sm;
            font-size: 0.8rem;
            font-weight: 500;
          }
        }
      }
    }
  }
}

// FAQ部分
.faq-section {
  .faq-list {
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: $spacing-lg;

    .faq-item {
      background: rgba($white, 0.95);
      border-radius: $border-radius-lg;
      box-shadow: $shadow-md, 0 0 20px rgba($accent-color, 0.08);
      border: 1px solid rgba($accent-color, 0.15);
      backdrop-filter: blur(10px);
      overflow: hidden;
      transition: transform 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: $shadow-lg, 0 0 25px rgba($accent-color, 0.15);
      }

      .faq-question {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: $spacing-xl $spacing-lg;
        cursor: pointer;
        background: $white;
        transition: background-color 0.2s ease;
        border-radius: $border-radius-lg $border-radius-lg 0 0;

        &:hover {
          background: linear-gradient(135deg,
            rgba($primary-color, 0.02) 0%,
            rgba($accent-color, 0.02) 100%);
        }

        .question-text {
          font-size: 1.15rem;
          font-weight: 600;
          color: $gray-800;
          margin: 0;
          flex: 1;
          padding-right: $spacing-lg;
          line-height: 1.5;
          word-wrap: break-word;
          display: flex;
          align-items: center;
          min-height: 28px;
        }

        .expand-icon {
          color: $primary-color;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          flex-shrink: 0;
          margin-top: 4px;
          background: rgba($primary-color, 0.1);
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;

          &.rotated {
            transform: rotate(180deg);
            background: rgba($accent-color, 0.15);
            color: $accent-color;
          }
        }
      }

      .faq-answer {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        background: linear-gradient(135deg,
          rgba($gray-50, 0.5) 0%,
          rgba($white, 0.8) 100%);

        &.expanded {
          max-height: 600px;
        }

        .answer-content {
          padding: $spacing-lg $spacing-lg $spacing-xl;

          p {
            font-size: 1.05rem;
            color: $gray-700;
            line-height: 1.7;
            margin: 0 0 $spacing-lg 0;
            font-weight: 400;
          }

          .answer-extra {
            ul {
              list-style: none;
              padding: 0;
              margin: 0;
              background: linear-gradient(135deg,
                rgba($primary-color, 0.03) 0%,
                rgba($accent-color, 0.03) 100%);
              border-radius: $border-radius-md;
              padding: $spacing-lg;
              border: 1px solid rgba($primary-color, 0.1);

              li {
                font-size: 0.95rem;
                color: $gray-700;
                margin-bottom: $spacing-sm;
                padding-left: $spacing-md;
                position: relative;
                line-height: 1.5;
                font-weight: 500;

                &:before {
                  content: '◦';
                  color: $accent-color;
                  position: absolute;
                  left: 0;
                  font-weight: 600;
                  font-size: 1.1rem;
                }

                &:last-child {
                  margin-bottom: 0;
                }
              }
            }
          }
        }
      }
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .category-seo-content {
    padding: $spacing-lg 0;
  }

  .section-header {
    .section-title {
      font-size: 1.5rem;
    }

    .section-subtitle {
      font-size: 1rem;
    }
  }

  .advantages-list {
    grid-template-columns: 1fr;

    .advantage-item {
      flex-direction: column;
      text-align: center;
    }
  }

  .scenarios-grid {
    grid-template-columns: 1fr;
  }

  .faq-list {
    gap: $spacing-md;

    .faq-item {
      .faq-question {
        padding: $spacing-lg $spacing-md;
        align-items: center;

        .question-text {
          font-size: 1.05rem;
          min-height: auto;
          padding-right: $spacing-md;
        }

        .expand-icon {
          width: 28px;
          height: 28px;
          margin-top: 0;
        }
      }

      .faq-answer {
        .answer-content {
          padding: $spacing-md;

          p {
            font-size: 1rem;
          }

          .answer-extra ul {
            padding: $spacing-md;

            li {
              font-size: 0.9rem;
            }
          }
        }
      }
    }
  }
}

// 平板端适配
@media (min-width: 769px) and (max-width: 1024px) {
  .advantages-list {
    grid-template-columns: 1fr;
  }

  .scenarios-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

// 滚动动画
section {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
